/**
 * App store (zustand vanilla + React hook). Implements contracts/store.ts.
 * Owned by the main loop — feature code reads it, never forks it.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { AppState, Insets, LayerFlags, Layout } from "@/contracts/store";
import { selectVisiblePlaces } from "./selectors";

const INTRO_KEY = "ld:intro-seen";

const readIntroSeen = (): boolean => {
  try {
    return typeof sessionStorage !== "undefined" && sessionStorage.getItem(INTRO_KEY) === "1";
  } catch {
    return false;
  }
};

const prefersReduced = (): boolean =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const DEFAULT_LAYERS: LayerFlags = {
  routes: true,
  future: true,
  contours: true,
  hillshade: true,
  rings: true,
  boundary: true,
  labels: true,
};

const ZERO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

export const appStore = createStore<AppState>()((set, get) => ({
  phase: "loading",
  loadedBytes: 0,
  totalBytes: 0,
  loadError: null,
  demReady: false,
  introSeen: readIntroSeen(),

  data: null,
  routes: null,
  geo: null,

  group: "A",
  category: null,
  query: "",
  sort: "time",

  selectedId: null,
  selectSource: null,
  hoveredId: null,
  bloomCategory: null,

  mood: "day", // day is the default (and only visible) mood; the LightIsland is hidden
  layers: { ...DEFAULT_LAYERS },
  layout: "desktop" as Layout,
  kiosk: false,
  sheet: "peek",
  insets: { ...ZERO_INSETS },
  reducedMotion: prefersReduced(),

  actions: {
    setPhase: (phase) => set({ phase }),
    setLoadProgress: (loadedBytes, totalBytes) => set({ loadedBytes, totalBytes }),
    setLoadError: (loadError) => set({ loadError }),
    setDemReady: (demReady) => set({ demReady }),
    setData: ({ data, routes, geo }) => set({ data, routes, geo }),
    markIntroSeen: () => {
      try {
        sessionStorage.setItem(INTRO_KEY, "1");
      } catch {
        /* private mode */
      }
      set({ introSeen: true });
    },

    setGroup: (group) => {
      const s = get();
      const sel = s.selectedId && s.data?.places.find((p) => p.id === s.selectedId);
      set({
        group,
        category: null,
        bloomCategory: null,
        selectedId: sel && sel.group === group ? s.selectedId : null,
        selectSource: sel && sel.group === group ? s.selectSource : null,
      });
    },
    setCategory: (category) => {
      const s = get();
      if (!category) return set({ category: null });
      const cat = s.data?.categories.find((c) => c.id === category);
      set({ category, group: cat ? cat.group : s.group });
    },
    setQuery: (query) => set({ query }),
    setSort: (sort) => set({ sort }),

    select: (id, src) => {
      if (id === null) return set({ selectedId: null, selectSource: null, phase: get().phase === "drive" ? "explore" : get().phase });
      const p = get().data?.places.find((x) => x.id === id);
      if (!p) return;
      set({ selectedId: id, selectSource: src ?? "list", group: p.group });
    },
    step: (dir) => {
      const s = get();
      const list = selectVisiblePlaces(s).filter((p) => !p.isSite);
      if (!list.length) return;
      const i = s.selectedId ? list.findIndex((p) => p.id === s.selectedId) : -1;
      const next = list[(i + dir + list.length) % list.length];
      set({ selectedId: next.id, selectSource: "key" });
    },
    hover: (hoveredId) => {
      if (get().hoveredId !== hoveredId) set({ hoveredId });
    },
    bloom: (bloomCategory) => set({ bloomCategory }),

    setMood: (mood) => set({ mood }),
    toggleLayer: (k, v) => set((s) => ({ layers: { ...s.layers, [k]: v ?? !s.layers[k] } })),
    setLayout: (layout) => set({ layout }),
    setKiosk: (kiosk) => set({ kiosk }),
    setSheet: (sheet) => set({ sheet }),
    setInsets: (i) => {
      const c = get().insets;
      if (c.top !== i.top || c.right !== i.right || c.bottom !== i.bottom || c.left !== i.left) set({ insets: { ...i } });
    },
    setReducedMotion: (reducedMotion) => set({ reducedMotion }),

    startDrive: () => {
      if (get().selectedId) set({ phase: "drive" });
    },
    stopDrive: () => {
      if (get().phase === "drive") set({ phase: "explore" });
    },
    resetAll: () =>
      set({
        group: "A",
        category: null,
        query: "",
        sort: "time",
        selectedId: null,
        selectSource: null,
        hoveredId: null,
        bloomCategory: null,
        mood: "day",
        layers: { ...DEFAULT_LAYERS },
        sheet: "peek",
        phase: get().phase === "loading" ? "loading" : "explore",
      }),
  },
}));

/** React hook with selector: const id = useApp(s => s.selectedId) */
export function useApp<T>(selector: (s: AppState) => T): T {
  return useStore(appStore, selector);
}

/** Stable actions object (never changes identity). */
export const actions = appStore.getState().actions;
export const getApp = appStore.getState;
